<template>
  <form class="checkout-login__email-form" @submit.prevent="form.handleSubmit">
    <TanStackFormInput
      name="email"
      :label="t('checkout.personalDetails.form.email')"
      type="email"
      :disabled="isLoading"
      :form="form"
      :required="true"
      data-test-id="email"
      autocomplete="email"
    />
    <form.Subscribe>
      <template #default="{ isSubmitting }">
        <CheckoutSubmitButton
          :is-loading="isSubmitting"
          :is-disabled="isLoading"
        >
          {{ t('checkout.steps.continue') }}
        </CheckoutSubmitButton>
      </template>
    </form.Subscribe>
  </form>
</template>

<script setup lang="ts">
import { useForm } from '@tanstack/vue-form'
import { useI18n } from 'vue-i18n'
import { watch } from 'vue'
import {
  createEmailSchema,
  type EmailFormData,
} from '../../../schemas/checkoutLogin'

const { t } = useI18n()

const props = defineProps<{
  isLoading: boolean
  email: string
}>()

const emit = defineEmits<{
  (e: 'submit', data: EmailFormData): void
  (e: 'email-change', email: string): void
}>()

const form = useForm({
  defaultValues: {
    email: props.email,
  },
  onSubmit: ({ value }) => {
    // Validate before submitting
    const schema = createEmailSchema(t)
    const result = schema.safeParse(value)
    if (!result.success) {
      return
    }
    emit('submit', value as EmailFormData)
  },
  validators: {
    onChange: createEmailSchema(t),
  },
})

const emailValue = form.useStore((state) => state.values.email)

watch(emailValue, (newEmail) => {
  emit('email-change', newEmail)
})
</script>

<style lang="postcss" scoped>
.checkout-login {
  &__email-form {
    @apply space-y-4;
  }

  &__submit {
    @apply w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-active focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50;
  }
}
</style>
